import React, {createContext, useContext} from 'react';
import type {Domain, PhaseColor, PortfolioData, ServiceArea} from './types';

/**
 * Shared portfolio data for every dashboard view. Mirrors the prototype's module-level
 * globals (PHASES / PHASE_COLORS / SERVICE_AREAS / DOMAINS) without leaking them globally.
 */
export interface DashboardData {
  phases: string[];
  phaseColors: Record<string, PhaseColor>;
  serviceAreas: ServiceArea[];
  domains: Domain[];
}

const DashboardContext = createContext<DashboardData | null>(null);

export function DashboardProvider({
  data,
  children
}: {
  data: PortfolioData;
  children: React.ReactNode;
}): React.ReactElement {
  // Retired domains are dropped here, before the service-area derivation below, so an area whose
  // only domain is deprecated disappears with it. Filtering at this single seam covers every view
  // (Program / Solutions / Matrix / Kanban / From-To) instead of each one remembering the flag.
  const domains = (data.domains ?? []).filter(d => !d.deprecated);
  // The service-area list is shared chrome (not scoped server-side), so a scoped viewer/owner would
  // otherwise see empty area headers for areas none of their domains belong to. Hide any area that
  // has no loaded domains (domains link to an area via `domain.area === serviceArea.id`).
  const areasWithDomains = new Set(domains.map(d => d.area).filter(Boolean));
  const serviceAreas = (data.serviceAreas ?? []).filter(sa => areasWithDomains.has(sa.id));

  const value: DashboardData = {
    phases: data.phases ?? [],
    phaseColors: data.phaseColors ?? {},
    serviceAreas,
    domains
  };
  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
}

export function useDashboard(): DashboardData {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error('useDashboard must be used within a DashboardProvider');
  return ctx;
}
